import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ProcessResponse } from "api/main/data-contracts";
import { createApi } from "util/ApiCreator";
import { useTestMode } from "context/TestModeContext";
import { mockProcesses } from "data/mockData";
import { adminTheme, statusColors } from "theme/adminTheme";
import {
  Box,
  Container,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  IconButton,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AccountTreeIcon from "@mui/icons-material/AccountTree";

export default function ProcessList() {
  const api = useRef(createApi("process"));
  const navigate = useNavigate();
  const { isTestMode } = useTestMode();
  const [processes, setProcesses] = useState<ProcessResponse[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isTestMode) {
      // Sort mock data by ID descending
      const sorted = [...mockProcesses].sort((a, b) => (b.id || 0) - (a.id || 0));
      setProcesses(sorted);
      setLoading(false);
    } else {
      getData();
    }
  }, [isTestMode]);

  async function getData() {
    setLoading(true);
    const data = await api.current.findAll();
    // Sort by ID descending (newest first)
    const sorted = (data || []).sort((a: ProcessResponse, b: ProcessResponse) => (b.id || 0) - (a.id || 0));
    setProcesses(sorted);
    setLoading(false);
  }

  function goToNewProcessCreation() {
    navigate("/process/new");
  }

  return (
    <Box sx={{ bgcolor: adminTheme.colors.backgroundAlt, minHeight: "100vh" }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 1,
                color: adminTheme.colors.textPrimary,
              }}
            >
              Procesai
            </Typography>
            <Typography
              variant="body"
              sx={{ color: adminTheme.colors.textSecondary }}
            >
              Valdykite savo procesų modelius
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={goToNewProcessCreation}
            sx={{
              background: adminTheme.gradients.primary,
              "&:hover": { background: adminTheme.gradients.primaryHover },
              px: 3,
              py: 1.5,
            }}
          >
            Naujas procesas
          </Button>
        </Box>

        {loading && <LinearProgress sx={{ mb: 3 }} />}

        {/* Stats Cards */}
        <Box sx={{ display: "flex", gap: 3, mb: 4, flexWrap: "wrap" }}>
          <Card
            sx={{
              flex: 1,
              minWidth: 200,
              p: 3,
              borderRadius: adminTheme.borderRadius.lg,
              border: `1px solid ${adminTheme.colors.border}`,
              boxShadow: "none",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: adminTheme.colors.textMuted, mb: 1 }}
            >
              Iš viso procesų
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 700, color: adminTheme.colors.textPrimary }}
            >
              {processes.length}
            </Typography>
          </Card>
          <Card
            sx={{
              flex: 1,
              minWidth: 200,
              p: 3,
              borderRadius: adminTheme.borderRadius.lg,
              border: `1px solid ${adminTheme.colors.border}`,
              boxShadow: "none",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: adminTheme.colors.textMuted, mb: 1 }}
            >
              Aktyvūs
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 700, color: adminTheme.colors.success }}
            >
              {processes.filter((p) => p.active).length}
            </Typography>
          </Card>
          <Card
            sx={{
              flex: 1,
              minWidth: 200,
              p: 3,
              borderRadius: adminTheme.borderRadius.lg,
              border: `1px solid ${adminTheme.colors.border}`,
              boxShadow: "none",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: adminTheme.colors.textMuted, mb: 1 }}
            >
              Neaktyvūs
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 700, color: adminTheme.colors.textSecondary }}
            >
              {processes.filter((p) => !p.active).length}
            </Typography>
          </Card>
        </Box>

        {/* Table */}
        <Card
          sx={{
            borderRadius: adminTheme.borderRadius.lg,
            border: `1px solid ${adminTheme.colors.border}`,
            boxShadow: "none",
            overflow: "hidden",
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: adminTheme.colors.backgroundAlt,
                    "& th": {
                      fontWeight: 600,
                      color: adminTheme.colors.textPrimary,
                      borderBottom: `2px solid ${adminTheme.colors.border}`,
                      py: 2,
                    },
                  }}
                >
                  <TableCell>Proceso pavadinimas</TableCell>
                  <TableCell>Versija</TableCell>
                  <TableCell>Žingsniai</TableCell>
                  <TableCell>Būsena</TableCell>
                  <TableCell align="right">Veiksmai</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {processes.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Box
                        sx={{
                          textAlign: "center",
                          py: 6,
                          color: adminTheme.colors.textMuted,
                        }}
                      >
                        <AccountTreeIcon
                          sx={{ fontSize: 48, mb: 2, opacity: 0.5 }}
                        />
                        <Typography>Nėra procesų</Typography>
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={goToNewProcessCreation}
                          sx={{ mt: 2 }}
                        >
                          Sukurti pirmą procesą
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  processes.map((process) => (
                    <TableRow
                      key={process.id}
                      sx={{
                        cursor: "pointer",
                        transition: adminTheme.transitions.fast,
                        "&:hover": {
                          bgcolor: "rgba(99, 102, 241, 0.04)",
                        },
                      }}
                      onClick={() => navigate(`/process/${process.id}`)}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: adminTheme.borderRadius.sm,
                              bgcolor: process.active
                                ? "rgba(99, 102, 241, 0.1)"
                                : adminTheme.colors.borderLight,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: process.active
                                ? adminTheme.colors.primary
                                : adminTheme.colors.textMuted,
                            }}
                          >
                            <AccountTreeIcon sx={{ fontSize: 20 }} />
                          </Box>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              color: adminTheme.colors.textPrimary,
                            }}
                          >
                            {process.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`v${process.version}`}
                          size="small"
                          sx={{
                            bgcolor: adminTheme.colors.backgroundAlt,
                            color: adminTheme.colors.textSecondary,
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {process.steps?.length || 0} žingsniai
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={process.active ? "Aktyvus" : "Neaktyvus"}
                          size="small"
                          sx={{
                            bgcolor: process.active
                              ? statusColors.active.bg
                              : statusColors.inactive.bg,
                            color: process.active
                              ? statusColors.active.text
                              : statusColors.inactive.text,
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Peržiūrėti">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/process/${process.id}`);
                            }}
                          >
                            <VisibilityIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Redaguoti">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/process/${process.id}`);
                            }}
                          >
                            <EditIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Container>
    </Box>
  );
}
